import { useContext, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { api, fmtMs, useLoad, usePoll } from "./api";
import { HelpContext, InstanceContext, MeContext, StatusContext, go, useInstance, useRoute, useStatus, type Instance } from "./ui";
import { Dashboard } from "./pages/Dashboard";
import { Station } from "./pages/Station";
import { Settings } from "./pages/Settings";
import { Admin } from "./pages/Admin";
import { Manual } from "./pages/Manual";
import { Developer } from "./pages/DevSystem";
import { PublicPlayer } from "./pages/PublicPlayer";
import { LibraryPage } from "./pages/library/Library";
import { Planned } from "./pages/Planned";
import { CategoriesPage } from "./pages/categories/Categories";
import { PLANNING_KEYS, PlanningPage } from "./pages/planning/PlanningPage";
import { SharedLog } from "./pages/planning/RulesAnalysis";
import { CONTENT_KEYS, ContentPage } from "./pages/content/Content";
import { VoicetrackPage } from "./pages/voicetrack/Voicetrack";
import { NetworkPage, currentStation, switchStation } from "./pages/network/Network";
import { Tour, startTour } from "./tour";
import "./styles.css";


function AcceptInvite({ token, onDone }: { token: string; onDone: () => void }) {
  const { data, error } = useLoad<any>(`/api/public/invite/${token}`);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Die beiden Passwörter stimmen nicht überein");
    try {
      const r = await api(`/api/public/invite/${token}`, { body: { password: pw } });
      await api("/api/login", { body: { login: r.login, password: pw } });
      onDone();
    } catch (x: any) {
      setErr(x.message);
    }
  };
  return (
    <div className="login">
      <form className="card stack" onSubmit={submit}>
        <div className="brand" style={{ color: "var(--ink)", padding: 0 }}><span className="dot">●</span> Radiocast</div>
        {error ? <div className="banner bad">{error}</div> : !data ? <div>Lade …</div> : (
          <>
            <div>Willkommen{data.name ? `, ${data.name}` : ""}! Lege dein Passwort fest. Dein Anmeldename ist <b>{data.email}</b>.</div>
            <label className="field"><span>Passwort (mind. 10 Zeichen)</span><input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus /></label>
            <label className="field"><span>Passwort wiederholen</span><input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
            {err && <div className="banner bad">{err}</div>}
            <button className="btn primary">Passwort setzen und anmelden</button>
          </>
        )}
      </form>
    </div>
  );
}

function Login({ onDone }: { onDone: () => void }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/api/login", { body: { login, password } });
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form className="card stack" onSubmit={submit}>
        <div className="brand" style={{ color: "var(--ink)", padding: 0 }}>
          <span className="dot">●</span> Radiocast
        </div>
        <label className="field">
          <span>Anmeldename</span>
          <input type="text" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Passwort</span>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && <div className="banner bad">{error}</div>}
        <button className="btn primary" disabled={busy}>Anmelden</button>
      </form>
    </div>
  );
}

/** Where the player listens: the output's listener link, else our own Icecast through Caddy. */
function listenUrl(o: any): string | null {
  if (o.listen_url) return o.listen_url;
  if (o.host === "127.0.0.1" || o.host === "localhost") return `/stream${o.mount}`;
  if (o.protocol === "icecast") return `${window.location.protocol === "https:" && o.port === 443 ? "https" : "http"}://${o.host}:${o.port}${o.mount}`;
  return null;
}

/** Header: which instance every page works for – big, with name and colour; the Verbund apart. */
function InstanceSwitcher({ list }: { list: any[] }) {
  const inst = useInstance();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const off = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, []);
  if (!inst) return null;
  const sw = (s: any) => <span className="sw" style={{ background: s.color }}>{s.kind === "verbund" ? "V" : (s.name || "?").replace(/^Radio\s+/i, "").slice(0, 1).toUpperCase()}</span>;
  const verbund = list.filter((s) => s.kind === "verbund");
  const stations = list.filter((s) => s.kind !== "verbund");
  const only = list.length < 2;
  return (
    <div className="inst" ref={ref}>
      <button onClick={() => !only && setOpen(!open)} title={only ? "Dein Zugang gilt für diesen Sender" : "Instanz wechseln – alle Seiten zeigen dann deren Daten"} style={only ? { cursor: "default" } : undefined}>
        {sw(inst)}
        <span><div className="kd">{inst.kind === "verbund" ? "Verbund · gemeinsames Programm" : "Sender"}</div><div className="nm">{inst.name}</div></span>
        {!only && <span style={{ marginLeft: "auto", color: "var(--ink-3)" }}>▾</span>}
      </button>
      {open && (
        <div className="menu">
          {verbund.map((s) => <button key={s.id} className={s.id === inst.id ? "cur" : ""} onClick={() => switchStation(s.id)}>{sw(s)}<span><b>{s.name}</b><div className="small muted">gemeinsame Musik und gemeinsames Programm</div></span></button>)}
          {verbund.length > 0 && stations.length > 0 && <div className="sep" />}
          {stations.length > 0 && <div className="hd">Sender</div>}
          {stations.map((s) => <button key={s.id} className={s.id === inst.id ? "cur" : ""} onClick={() => switchStation(s.id)}>{sw(s)}<span><b>{s.name}</b>{!s.enabled && <span className="small muted"> (aus)</span>}</span></button>)}
        </div>
      )}
    </div>
  );
}

function PlayerBar({ list }: { list: any[] }) {
  const status = useStatus();
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const { help, setHelp } = useContext(HelpContext);
  const { data: outputs } = useLoad<any[]>("/api/outputs");
  // External http streams go through the API (no mixed content on the https page).
  const choices = (outputs ?? []).filter((o) => o.enabled).map((o) => {
    const u = listenUrl(o);
    return { id: o.id, name: o.name, url: u && !u.startsWith("/") && !u.startsWith("https:") ? `/api/listen/${o.id}` : u };
  }).filter((c) => c.url);
  const [choice, setChoice] = useState<string>("");
  const current = choices.find((c) => c.id === choice) ?? choices[0];
  const e = status?.engine;
  const np = e?.now_playing;
  const live = e?.on_air?.kind === "stream";
  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (playing) {
      a.pause();
      a.removeAttribute("src");
      a.load();
      setPlaying(false);
    } else {
      if (!current?.url) return;
      // Fresh URL each time: no stale buffer from minutes ago.
      a.src = current.url + (current.url.includes("?") ? "&" : "?") + `t=${Date.now()}`;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };
  return (
    <header className="top">
      <InstanceSwitcher list={list} />
      {useInstance()?.kind === "verbund" ? <div className="player"><span className="muted">Der Verbund hat keinen eigenen Sendeweg – die Sender übernehmen sein Programm. Zum Anhören einen Sender wählen.</span></div> : <div className="player">
        <button className="btn primary" onClick={toggle} disabled={!current} title={current ? `${current.name} anhören` : "Kein aktiver Ausgang"}>{playing ? "■ Stopp" : "▶ Anhören"}</button>
        <audio ref={audio} preload="none" onError={() => setPlaying(false)} />
        {choices.length > 1 && (
          <select style={{ width: 160 }} value={current?.id} onChange={(e) => { setChoice(e.target.value); if (playing) toggle(); }} title="Welchen Ausgang anhören">
            {choices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {live ? <span className="badge live">LIVE</span> : e?.state === "läuft" ? <span className="badge ok">AUTO</span> : <span className="badge bad">OFF</span>}
        <div className="np">
          {live ? (
            <b>{e.on_air.name}{e.on_air.title ? ` – ${e.on_air.title}` : ""}</b>
          ) : np ? (
            <>
              <b>{np.item.artist ? `${np.item.artist} – ` : ""}{np.item.title}</b>{" "}
              <span className="muted small">{fmtMs(np.pos_ms - (np.item.marks?.cue_in ?? 0))} / {fmtMs(np.len_ms)}</span>
            </>
          ) : (
            <span className="muted">{e?.state === "läuft" ? "Programm wird geladen …" : "Engine " + (e?.state ?? "…")}</span>
          )}
        </div>
      </div>}
      <label className="switch small" title="Tipps auf jeder Seite einblenden">
        <input type="checkbox" checked={help} onChange={(ev) => setHelp(ev.target.checked)} />
        <span className="track" />
        <span>Hilfe</span>
      </label>
    </header>
  );
}

/** Branding: interface name and accent colour (Verwaltung › System). */
function useBranding() {
  const [b, setB] = useState<any>({});
  useEffect(() => {
    const load = () => api("/api/branding").then((x) => {
      setB(x ?? {});
      if (x?.accent) document.documentElement.style.setProperty("--accent", x.accent);
      document.title = x?.name || "Radiocast";
    }).catch(() => {});
    load();
    window.addEventListener("rc-branding", load);
    return () => window.removeEventListener("rc-branding", load);
  }, []);
  return b;
}

/** Sidebar: few clear groups (Sendung, Programm, Sender, Verbund, Auswertung, System), one symbol style, no doubles. */
function Sidebar({ route, me, onLogout, preview }: { route: string[]; me: any; onLogout: () => void; preview?: boolean }) {
  const brand = useBranding();
  const inst = useInstance();
  const verbund = inst?.kind === "verbund";
  const st = preview ? "main" : currentStation();
  const here = route.join("/");
  const own = ["einstellungen/ausgaenge", "einstellungen/monitor", "einstellungen/klang"]; // have their own menu entries
  const link = (path: string, icon: string, label: string, also: string[] = []) => {
    const active = [path, ...also].some((p) => here === p || here.startsWith(`${p}/`)) && !(path === "einstellungen" && own.some((o) => here.startsWith(o)));
    return <a key={path} className={`nav${active ? " active" : ""}`} href={`#/${path}`}><span className="ni">{icon}</span>{label}</a>;
  };
  const group = (label: string, items: (JSX.Element | false)[]) => {
    const shown = items.filter(Boolean);
    return shown.length ? <div className="group"><div className="label">{label}</div>{shown}</div> : null;
  };
  const admin = me?.role === "admin";
  return (
    <nav className="side">
      <div className="brand"><span className="dot">●</span> {brand.name || "Radiocast"}</div>
      {group("Sendung", [
        !verbund && link("dashboard", "◉", "Dashboard"),
        link("plan/sendeplan", "☰", "Sendeplan", ["plan/log"]),
        link("plan/voicetracking", "🎙", "Voicetracking"),
        !verbund && link(`sender/${st}/sendung`, "⏯", "Engine-Steuerung"),
        !verbund && link(`sender/${st}/studio`, "◎", "Studio"),
      ])}
      {group("Programm", [
        link("bibliothek", "♪", "Bibliothek & Rotation", ["plan/kategorien"]),
        link("plan/uhren", "◷", "Uhren"),
        link("plan/wochenraster", "▦", "Wochenraster"),
        link("plan/regeln", "⇆", "Regeln"),
        !verbund && link("plan/imaging", "✦", "Imaging & Drop-ins"),
        !verbund && link("plan/werbung", "€", "Werbung"),
        !verbund && link("plan/nachrichten", "✉", "Nachrichten"),
      ])}
      {group("Sender", verbund ? [] : [
        link(`sender/${st}/uebersicht`, "⌂", "Übersicht"),
        link("einstellungen/ausgaenge", "⇪", "Streams & Encoder", [`sender/${st}/streams`]),
        link("einstellungen/monitor", "⎆", "Stream-Monitor"),
        link("einstellungen/klang", "∿", "Klang"),
        link(`sender/${st}/moderatoren`, "☺", "Moderatoren"),
      ])}
      {group("Verbund", [
        link("verbund/uebersicht", "⊞", "Übersicht", ["verbund"]),
        link("verbund/sender", "⊕", "Sender"),
        link("verbund/regeln", "⇄", "Übernahmen"),
        link("verbund/tausch", "⇋", "Kategorie-Tausch"),
        link("verbund/streams", "⇪", "Alle Streams"),
        link("verbund/nachweis", "✓", "Sendenachweis"),
      ])}
      {group("Auswertung", [
        !verbund && link(`sender/${st}/statistik`, "▤", "Statistik"),
        link("plan/analyse", "◔", "Analyse"),
      ])}
      {group("System", [
        link("einstellungen", "⚙", "Einstellungen", []),
        !verbund && link(`sender/${st}/oeffentlich`, "◫", "Öffentliche Seite"),
        !verbund && link(`sender/${st}/webhooks`, "↯", "Webhooks"),
        admin && link("verwaltung", "⚿", "Verwaltung"),
        link("entwickler", "⌘", "Entwickler"),
        link("handbuch", "?", "Handbuch"),
      ])}
      <div className="bottom">
        <div className="user">{me?.name} · {me?.roleLabel}</div>
        <a className="nav" href="#/" onClick={(e) => { e.preventDefault(); startTour(); }}><span className="ni">▶</span>Einführungstour</a>
        <a className="nav" href="#/" onClick={(e) => { e.preventDefault(); onLogout(); }}><span className="ni">⏻</span>Abmelden</a>
      </div>
    </nav>
  );
}

/** Without login (#/vorschau): the frame with menu and header on sample data – to check the layout, nothing else loads. */
function LayoutPreview() {
  const route = useRoute();
  const list = [
    { id: "verbund", name: "Verbund", color: "#5b4bb7", kind: "verbund", enabled: true },
    { id: "main", name: "Radio Geretsried", color: "#e8673c", kind: "station", enabled: true },
    { id: "b6", name: "Radio B6 - Test", color: "#2f6fb1", kind: "station", enabled: true },
  ];
  const inst: Instance = { id: "main", name: "Radio Geretsried", color: "#e8673c", kind: "station" };
  return (
    <MeContext.Provider value={{ name: "Vorschau", role: "admin", roleLabel: "Verwaltung" }}>
      <InstanceContext.Provider value={inst}>
        <div className="app">
          <Sidebar route={route.slice(1)} me={{ name: "Vorschau", role: "admin", roleLabel: "Verwaltung" }} onLogout={() => {}} preview />
          <header className="top"><InstanceSwitcher list={list} /><div className="player"><span className="muted">Vorschau der Bedienoberfläche (ohne Daten)</span></div></header>
          <main className="main"><div className="page-head"><div><h1>Vorschau</h1><div className="muted"><span className="inst-chip" style={{ borderColor: inst.color }}><i style={{ background: inst.color }} />{inst.name}</span>Menü und Kopf ohne Anmeldung</div></div></div></main>
        </div>
      </InstanceContext.Provider>
    </MeContext.Provider>
  );
}

function Toasts() {
  const [t, setT] = useState<{ text: string; bad: boolean } | null>(null);
  useEffect(() => {
    let timer: number;
    const on = (e: Event) => {
      setT((e as CustomEvent).detail);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setT(null), 3500);
    };
    window.addEventListener("rc-toast", on);
    return () => window.removeEventListener("rc-toast", on);
  }, []);
  return t ? <div className={`toast${t.bad ? " bad" : ""}`}>{t.text}</div> : null;
}

function Shell({ me, onLogout }: { me: any; onLogout: () => void }) {
  const route = useRoute();
  const { data: status } = usePoll("/api/status", 2000);
  const { data: list } = useLoad<any[]>("/api/stations");
  const cur = currentStation();
  const found = (list ?? []).find((s) => s.id === cur) ?? (list ?? [])[0];
  const inst: Instance | null = found ? { id: found.id, name: found.name, color: found.color ?? "#2f6fb1", kind: found.kind === "verbund" ? "verbund" : "station" } : null;
  // The cookie names a station this user may not use (or one that is gone): switch to the first allowed one.
  useEffect(() => { if (list?.length && found && found.id !== cur) switchStation(found.id); }, [list?.length, found?.id, cur]);
  const [help, setHelpState] = useState(() => {
    try {
      return localStorage.getItem("rc-help") === "1";
    } catch {
      return false;
    }
  });
  const setHelp = (v: boolean) => {
    setHelpState(v);
    try {
      localStorage.setItem("rc-help", v ? "1" : "0");
    } catch {
      /* private mode */
    }
  };
  const [section, ...rest] = route;
  let page;
  switch (section) {
    case "dashboard": page = <Dashboard />; break;
    case "sender": page = <Station parts={rest} />; break;
    case "bibliothek": page = <LibraryPage parts={rest} />; break;
    case "entwickler": page = <Developer />; break;
    // Kategorien & Rotation live in the library now (old links lead there; board and analysis stay as views).
    case "plan": page = rest[0] === "kategorien" ? (["board", "analyse", "codierung", "ziele"].includes(rest[1] ?? "") ? <CategoriesPage parts={rest.slice(1)} /> : <LibraryPage parts={["rotation", ...rest.slice(1)]} />) : PLANNING_KEYS.includes(rest[0]) ? <PlanningPage which={rest[0]} /> : CONTENT_KEYS.includes(rest[0]) ? <ContentPage which={rest[0]} /> : rest[0] === "voicetracking" ? <VoicetrackPage /> : <Planned which={rest[0]} />; break;
    case "handbuch": page = <Manual chapter={rest[0]} />; break;
    case "einstellungen": page = <Settings tab={rest[0]} />; break;
    case "verwaltung": page = <Admin tab={rest[0]} />; break;
    case "verbund": page = <NetworkPage tab={rest[0]} />; break;
    default: page = <Dashboard />;
  }
  return (
    <MeContext.Provider value={me}>
     <InstanceContext.Provider value={inst}>
      <HelpContext.Provider value={{ help, setHelp }}>
        <StatusContext.Provider value={status}>
          <div className="app">
            <Sidebar route={route} me={me} onLogout={onLogout} />
            <PlayerBar list={list ?? []} />
            <main className="main">{page}</main>
          </div>
          <Toasts />
          <Tour />
        </StatusContext.Provider>
      </HelpContext.Provider>
     </InstanceContext.Provider>
    </MeContext.Provider>
  );
}

function App() {
  const route = useRoute();
  const [me, setMe] = useState<any>(undefined);
  const check = () => api("/api/me").then((r) => setMe(r.loggedIn ? r.user : null)).catch(() => setMe(null));
  useEffect(() => {
    check();
    const out = () => setMe(null);
    window.addEventListener("rc-logout", out);
    return () => window.removeEventListener("rc-logout", out);
  }, []);
  // The public player and its widgets work without login.
  const player = /^\/player\/([\w-]+)/.exec(window.location.pathname);
  if (player) return <PublicPlayer slug={player[1]} widget={new URLSearchParams(window.location.search).get("widget")} />;
  // Shared read-only links work without login.
  const shared = /^#\/geteilt\/([\w-]+)/.exec(window.location.hash);
  if (shared) return <SharedLog token={shared[1]} />;
  if (route[0] === "vorschau") return <LayoutPreview />;
  // Invitation link: set a password without being signed in.
  const invite = /^#\/einladung\/([\w-]+)/.exec(window.location.hash);
  if (invite) return <AcceptInvite token={invite[1]} onDone={() => { window.location.hash = "#/dashboard"; check(); }} />;
  if (me === undefined) return null;
  if (!me) return <Login onDone={check} />;
  return (
    <Shell
      me={me}
      onLogout={async () => {
        await api("/api/logout", { method: "POST" }).catch(() => {});
        setMe(null);
        go("dashboard");
      }}
    />
  );
}

createRoot(document.getElementById("root")!).render(<App />);
